import { cn } from "@/lib/utils";
import type { Player, SkillKey, LonerValue } from "../types";
import { PlayerCard } from "./PlayerCard";
import { useLanguage } from "../i18n";

export interface PlayersSectionProps {
  players: Record<string, Player>;
  onAddPlayer: () => void;
  onRemovePlayer: (id: string) => void;
  onUpdatePlayerNumber: (id: string, number: number) => void;
  onToggleSkill: (playerId: string, skill: SkillKey) => void;
  onSetLoner: (playerId: string, enabled: boolean) => void;
  onSetLonerValue: (playerId: string, value: LonerValue) => void;
  className?: string;
}

export function PlayersSection({
  players,
  onAddPlayer,
  onRemovePlayer,
  onUpdatePlayerNumber,
  onToggleSkill,
  onSetLoner,
  onSetLonerValue,
  className,
}: PlayersSectionProps) {
  const { t } = useLanguage();
  const playerList = Object.values(players);

  return (
    <section
      className={cn(
        "bg-bb-gray-dark border-2 border-bb-gray-mid border-l-4 border-l-bb-blood-red mb-6",
        className
      )}
    >
      <div
        className={cn(
          "flex justify-between items-center",
          "bg-bb-gray-mid px-4 py-3",
          "border-b border-bb-gold/30"
        )}
      >
        <h2 className="font-condensed text-xl font-bold uppercase tracking-wider text-bb-gold">
          {t.players}
        </h2>
        <button
          type="button"
          onClick={onAddPlayer}
          className={cn(
            "font-condensed text-sm font-semibold uppercase tracking-wide",
            "px-3 py-1.5 transition-all",
            "bg-bb-gray-dark text-bb-bone-white border border-bb-gray-mid",
            "hover:border-bb-gold hover:text-bb-gold"
          )}
        >
          {t.addPlayer}
        </button>
      </div>

      <div className="p-4">
        {playerList.length === 0 ? (
          <p className="text-bb-bone-white/60 text-center py-4">
            {t.noPlayer}
          </p>
        ) : (
          playerList.map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              onUpdateNumber={(number) => onUpdatePlayerNumber(player.id, number)}
              onToggleSkill={(skill) => onToggleSkill(player.id, skill)}
              onSetLoner={(enabled) => onSetLoner(player.id, enabled)}
              onSetLonerValue={(value) => onSetLonerValue(player.id, value)}
              onRemove={() => onRemovePlayer(player.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}
